import jsPDF from "jspdf";
import maplibregl from "maplibre-gl";
import { MapRef } from "react-map-gl/maplibre";

import { getRanaApiV1 } from "@nens/rana/api-v1";
import { createMapLibreTransformFunction } from "@nens/react-map-gl/utils";
import { Extent, downloadData } from "@nens/shared-utils";

import { CaptureArea } from "../types/index";

function createOffscreenContainer(width: number, height: number): HTMLDivElement {
  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.left = "-10000px";
  container.style.top = "0";
  container.style.width = width + "px";
  container.style.height = height + "px";
  document.body.appendChild(container);
  return container;
}

interface ExportOptions {
  width: number;
  height: number;
  format: "png" | "jpeg" | "pdf";
  fileName: string;
}

type ExportBlobOptions = Omit<ExportOptions, "fileName">;

type ExportMapState = {
  style: maplibregl.StyleSpecification;
  bounds: Extent;
  bearing: number;
  pitch: number;
};

function createExportMapStateFromCaptureArea(
  sourceMap: maplibregl.Map,
  captureArea: CaptureArea
): ExportMapState {
  const northWest = sourceMap.unproject([captureArea.x, captureArea.y]);
  const northEast = sourceMap.unproject([captureArea.x + captureArea.width, captureArea.y]);
  const southWest = sourceMap.unproject([captureArea.x, captureArea.y + captureArea.height]);
  const southEast = sourceMap.unproject([
    captureArea.x + captureArea.width,
    captureArea.y + captureArea.height,
  ]);

  return {
    style: sourceMap.getStyle(),
    bounds: [
      Math.min(northWest.lng, northEast.lng, southWest.lng, southEast.lng),
      Math.min(northWest.lat, northEast.lat, southWest.lat, southEast.lat),
      Math.max(northWest.lng, northEast.lng, southWest.lng, southEast.lng),
      Math.max(northWest.lat, northEast.lat, southWest.lat, southEast.lat),
    ],
    bearing: sourceMap.getBearing(),
    pitch: sourceMap.getPitch(),
  };
}

function createExportMapStateFromMap(sourceMap: maplibregl.Map): ExportMapState {
  return {
    style: sourceMap.getStyle(),
    bounds: sourceMap.getBounds().toArray().flat() as Extent,
    bearing: sourceMap.getBearing(),
    pitch: sourceMap.getPitch(),
  };
}

async function renderExportCanvas(state: ExportMapState, width: number, height: number) {
  const renderWidth = Math.max(1, Math.round(width));
  const renderHeight = Math.max(1, Math.round(height));
  const offscreenContainer = createOffscreenContainer(renderWidth, renderHeight);

  const exportMap = new maplibregl.Map({
    ...state,
    container: offscreenContainer,
    fitBoundsOptions: { padding: 0, linear: true },
    interactive: false,
    attributionControl: false,
    transformRequest: createMapLibreTransformFunction(getRanaApiV1().accessToken + "", false),
  });

  // The try starts right after creating the map so the WebGL context and container are always
  // cleaned up, also when loading fails.
  try {
    // Wait for the map to finish loading and rendering
    await new Promise<void>((resolve, reject) => {
      exportMap.once("idle", () => resolve());
      exportMap.once("error", ({ error }) => {
        // Will also be thrown if style.json is not found which is expected behaviour
        if (error.status === 404) {
          return;
        }
        reject(new Error("Map export failed"));
      });
    });

    const mapCanvas = exportMap.getCanvas();

    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = width;
    exportCanvas.height = height;
    const exportCtx = exportCanvas.getContext("2d");

    if (!exportCtx) {
      throw new Error("Failed to get export canvas context");
    }

    exportCtx.drawImage(mapCanvas, 0, 0, width, height);

    return exportCanvas;
  } finally {
    exportMap.remove();
    offscreenContainer.remove();
  }
}

function createImageBlob(
  canvas: HTMLCanvasElement,
  mimeType: "image/png" | "image/jpeg",
  quality: number
) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to generate image blob"));
          return;
        }

        resolve(blob);
      },
      mimeType,
      quality
    );
  });
}

function createPdfDocument(exportCanvas: HTMLCanvasElement, width: number, height: number) {
  const pdf = new jsPDF({
    orientation: width > height ? "landscape" : "portrait",
    unit: "px",
    format: [width, height],
  });
  pdf.addImage(exportCanvas.toDataURL("image/png"), "PNG", 0, 0, width, height);
  return pdf;
}

async function exportMapBlobFromState(
  state: ExportMapState,
  opts: ExportBlobOptions
): Promise<Blob> {
  const { width, height, format } = opts;
  const exportCanvas = await renderExportCanvas(state, width, height);

  if (format === "pdf") {
    return createPdfDocument(exportCanvas, width, height).output("blob");
  }

  const mimeType = format === "jpeg" ? "image/jpeg" : "image/png";
  return createImageBlob(exportCanvas, mimeType, format === "jpeg" ? 0.95 : 1);
}

async function exportMapFromState(state: ExportMapState, opts: ExportOptions) {
  const { width, height, format, fileName } = opts;

  if (format === "pdf") {
    const exportCanvas = await renderExportCanvas(state, width, height);

    createPdfDocument(exportCanvas, width, height).save(fileName);
    return;
  }

  const blob = await exportMapBlobFromState(state, {
    width,
    height,
    format,
  });
  downloadData(blob, fileName);
}

// Export a specific section of the map, defined by a rectangle in pixel coordinates and download
export async function exportMapSection(
  mapRef: MapRef,
  captureArea: CaptureArea,
  opts: ExportOptions
) {
  const sourceMap = mapRef.getMap();

  await exportMapFromState(createExportMapStateFromCaptureArea(sourceMap, captureArea), opts);
}

// Export the entire map, using the current viewport (or overridden state) and download
export async function exportMap(
  mapRef: MapRef,
  mapStateOverride: Partial<ExportMapState>,
  opts: ExportOptions
) {
  const sourceMap = mapRef.getMap();
  const mapState = {
    ...createExportMapStateFromMap(sourceMap),
    ...mapStateOverride,
  };
  await exportMapFromState(mapState, opts);
}

// Export the entire map, using the current viewport (or overridden state) and return a blob
export async function exportMapBlob(
  mapRef: MapRef,
  mapStateOverride: Partial<ExportMapState>,
  opts: ExportBlobOptions
) {
  const sourceMap = mapRef.getMap();
  const mapState = {
    ...createExportMapStateFromMap(sourceMap),
    ...mapStateOverride,
  };

  return exportMapBlobFromState(mapState, opts);
}
